import React from 'react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { Flex, Text } from '@chakra-ui/react';
import { Plus } from 'phosphor-react';
import { IKanbanColumn, IKanbanCard, IColunaPermMap } from '../../hooks/useKanban';
import { KanbanColumn } from './KanbanColumn';
import { useAgora } from './chegadaNaColuna';

interface KanbanBoardProps {
  columns: IKanbanColumn[];
  canManage: boolean;
  canEditProtected?: boolean;
  colunaPerms?: IColunaPermMap;
  // Espelho read-only (quadro de módulo, task #757): sem drag, sem criar/editar.
  isReadOnly?: boolean;
  /** Faixa opcional no topo de uma coluna — quem decide qual e o quê é o domínio. */
  // eslint-disable-next-line no-unused-vars
  topoDaColuna?: (column: IKanbanColumn) => React.ReactNode;
  /** Selo "Novo" e "chegou há…" nos cards (quadro do evento). */
  destacarRecentes?: boolean;
  // eslint-disable-next-line no-unused-vars
  onCardClick: (card: IKanbanCard) => void;
  // eslint-disable-next-line no-unused-vars
  onAddCardClick: (column: IKanbanColumn) => void;
  // eslint-disable-next-line no-unused-vars
  onEditColumn: (column: IKanbanColumn) => void;
  // eslint-disable-next-line no-unused-vars
  onArchiveColumn: (columnId: number) => void;
  // eslint-disable-next-line no-unused-vars
  onDragEnd: (result: DropResult) => void;
  onAddColumn?: () => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  columns,
  canManage,
  canEditProtected = false,
  colunaPerms,
  isReadOnly = false,
  topoDaColuna,
  destacarRecentes = false,
  onCardClick,
  onAddCardClick,
  onEditColumn,
  onArchiveColumn,
  onDragEnd,
  onAddColumn,
}) => {
  const agora = useAgora(destacarRecentes);
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="board" type="column" direction="horizontal">
        {provided => (
          <Flex
            ref={provided.innerRef}
            {...provided.droppableProps}
            overflowX="auto"
            pb={4}
            minH="calc(100vh - 200px)"
            alignItems="flex-start"
            gap={4}
            px={1}
          >
            {columns.map((column, index) => (
              <KanbanColumn
                key={column.id}
                column={column}
                index={index}
                canManage={canManage}
                canEditProtected={canEditProtected}
                canCreateCard={!colunaPerms || colunaPerms[column.titulo]?.criar_card !== false}
                isReadOnly={isReadOnly}
                topo={topoDaColuna?.(column)}
                destacarRecentes={destacarRecentes}
                agora={agora}
                onCardClick={onCardClick}
                onAddCardClick={onAddCardClick}
                onEditColumn={onEditColumn}
                onArchiveColumn={onArchiveColumn}
              />
            ))}
            {provided.placeholder}

            {/* Card de adicionar coluna */}
            {!isReadOnly && canManage && onAddColumn && (
              <Flex
                minW="320px"
                maxW="320px"
                h="120px"
                borderRadius="16px"
                border="2px dashed"
                borderColor="gray.200"
                cursor="pointer"
                align="center"
                justify="center"
                direction="column"
                gap={2}
                flexShrink={0}
                transition="all 0.2s ease"
                _hover={{ borderColor: 'gray.300', bg: 'white' }}
                onClick={onAddColumn}
              >
                <Flex
                  w="36px"
                  h="36px"
                  borderRadius="full"
                  bg="gray.100"
                  align="center"
                  justify="center"
                >
                  <Plus size={16} color="#9CA3AF" weight="bold" />
                </Flex>
                <Text fontSize="13px" color="gray.400" fontWeight="500">
                  Nova coluna
                </Text>
              </Flex>
            )}
          </Flex>
        )}
      </Droppable>
    </DragDropContext>
  );
};
